import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { RealtimeProvider } from "@/context/RealtimeContext";
import Navbar from "@/components/Navbar";
import BottomNav from "@/components/BottomNav";
import ProtectedRoute from "@/components/ProtectedRoute";
import OnboardingTour from "@/components/OnboardingTour";
import { useAuth } from "@/context/AuthContext";

import HomePage from "@/pages/HomePage";
import FeedPage from "@/pages/FeedPage";
import EventDetailPage from "@/pages/EventDetailPage";
import CreateEventPage from "@/pages/CreateEventPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import MyEventsPage from "@/pages/MyEventsPage";
import ManageEventPage from "@/pages/ManageEventPage";
import EditEventPage from "@/pages/EditEventPage";
import ProfilePage from "@/pages/ProfilePage";
import PublicProfilePage from "@/pages/PublicProfilePage";
import NotificationsPage from "@/pages/NotificationsPage";
import MessagesPage from "@/pages/MessagesPage";
import MessageThreadPage from "@/pages/MessageThreadPage";
import MapPage from "@/pages/MapPage";
import AdminRoute from "@/components/AdminRoute";
import AdminShell from "@/pages/admin/AdminShell";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminReports from "@/pages/admin/AdminReports";
import AdminFeedback from "@/pages/admin/AdminFeedback";
import AdminBackfill from "@/pages/admin/AdminBackfill";
import SettingsPage from "@/pages/SettingsPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import Footer from "@/components/Footer";
import { useLocation } from "react-router-dom";

// Routes (or route prefixes) where the global footer is shown. The feed
// is intentionally NOT in this list — it has heavy filters + the mobile
// BottomNav, so the trust-and-reach links live elsewhere (home, signup,
// settings). Keeps the feed canvas focused.
const FOOTER_ROUTES = ["/", "/signup"];

function shouldShowFooter(pathname) {
  return FOOTER_ROUTES.includes(pathname);
}

function AppShell() {
  const { pathname } = useLocation();
  const showFooter = shouldShowFooter(pathname);
  const { onboardingOpen, closeOnboarding, completeOnboarding } = useAuth();
  return (
    <div className="min-h-screen bg-paper text-ink">
      <Navbar />
      <main className="pb-nav">
        <Outlet />
      </main>
      {showFooter && <Footer />}
      <BottomNav />
      <OnboardingTour
        open={onboardingOpen}
        onClose={closeOnboarding}
        onComplete={completeOnboarding}
      />
      <Toaster
        theme="light"
        position="top-center"
        toastOptions={{
          style: {
            background: "#F7F2E5",
            border: "2.5px solid #0A0A0A",
            color: "#0A0A0A",
            borderRadius: "1rem",
            boxShadow: "3px 3px 0 0 #0A0A0A",
            fontFamily: "Outfit, sans-serif",
            fontWeight: 500,
          },
        }}
      />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <RealtimeProvider>
        <BrowserRouter>
          <Routes>
            <Route element={<AppShell />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/feed" element={<FeedPage />} />
              <Route path="/map"  element={<MapPage />} />
              <Route path="/events/:id" element={<EventDetailPage />} />
              <Route path="/u/:handle" element={<PublicProfilePage />} />
              <Route path="/events/:id/manage" element={<ProtectedRoute><ManageEventPage /></ProtectedRoute>} />
              <Route path="/events/:id/edit"   element={<ProtectedRoute><EditEventPage   /></ProtectedRoute>} />
              <Route path="/create"            element={<ProtectedRoute><CreateEventPage /></ProtectedRoute>} />
              <Route path="/my-events"         element={<ProtectedRoute><MyEventsPage    /></ProtectedRoute>} />
              <Route path="/profile"           element={<ProtectedRoute><ProfilePage     /></ProtectedRoute>} />
              <Route path="/settings"          element={<ProtectedRoute><SettingsPage    /></ProtectedRoute>} />
              <Route path="/notifications"     element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
              <Route path="/messages"          element={<ProtectedRoute><MessagesPage      /></ProtectedRoute>} />
              <Route path="/messages/:otherId" element={<ProtectedRoute><MessageThreadPage /></ProtectedRoute>} />
              <Route path="/login"  element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password"  element={<ResetPasswordPage />} />
              <Route path="/terms"   element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />

              <Route path="/admin" element={<AdminRoute><AdminShell /></AdminRoute>}>
                <Route index           element={<AdminDashboard />} />
                <Route path="users"    element={<AdminUsers />} />
                <Route path="events"   element={<AdminEvents />} />
                <Route path="reports"  element={<AdminReports />} />
                <Route path="feedback" element={<AdminFeedback />} />
                <Route path="backfill-locations" element={<AdminBackfill />} />
              </Route>

              <Route path="*" element={<Navigate to="/feed" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </RealtimeProvider>
    </AuthProvider>
  );
}
